import { useState, useEffect, useCallback, useMemo, useImperativeHandle, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FuseAuthProviderComponentProps, FuseAuthProviderState } from '@fuse/core/FuseAuthProvider/types/FuseAuthTypes';
import { clearAuthStorage, authRefreshToken, authSignIn, authUpdateDbUser } from '@auth/authApi';
import { getAuthItem, isPersistentSession, setAuthItem } from '@auth/authStorage';
import { User } from '../../user';
import { removeGlobalHeaders, setGlobalHeaders } from '@/utils/api';
import { isTokenValid, getTimeUntilExpiry } from './utils/jwtUtils';
import JwtAuthContext from '@auth/services/jwt/JwtAuthContext';
import { JwtAuthContextType } from '@auth/services/jwt/JwtAuthContext';
import { HTTPError } from 'ky';
import { updateApiClientAuth } from '@/api/client';
import { navigationService } from '@/api/services/navigationService';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';

export type JwtSignInPayload = {
	userName: string;
	password: string;
	/**
	 * When true, tokens persist in localStorage and the backend issues a 30-day cookie;
	 * when false (default), they go to sessionStorage and the backend issues a session
	 * cookie cleared on browser close.
	 */
	rememberMe?: boolean;
};

export type JwtSignUpPayload = {
	displayName: string;
	email: string;
	password: string;
};

type AuthFormError = {
	message: string;
	status?: number;
	data: {
		type: 'root';
		message: string;
	}[];
};

type AuthSession = {
	user: User;
	access_token: string;
};

type RefreshSession = {
	user?: User;
	access_token: string;
};

const AUTH_REFRESH_BYPASS_HEADER = 'X-Skip-Auth-Refresh';
const DEFAULT_LOGIN_REDIRECT_URL = '/dashboard';

function createAuthError(message: string, status?: number): AuthFormError {
	return {
		message,
		status,
		data: [
			{
				type: 'root',
				message
			}
		]
	};
}

function extractAuthErrorMessage(payload: unknown, fallbackMessage: string): string {
	if (!payload) {
		return fallbackMessage;
	}

	if (typeof payload === 'string' && payload.trim()) {
		return payload;
	}

	if (typeof payload !== 'object') {
		return fallbackMessage;
	}

	if ('error' in payload && typeof payload.error === 'string' && payload.error.trim()) {
		return payload.error;
	}

	if ('message' in payload && typeof payload.message === 'string' && payload.message.trim()) {
		return payload.message;
	}

	if (
		'data' in payload &&
		Array.isArray(payload.data) &&
		typeof payload.data[0]?.message === 'string' &&
		payload.data[0].message.trim()
	) {
		return payload.data[0].message;
	}

	return fallbackMessage;
}

async function parseAuthResponsePayload(response: Response, fallbackMessage: string): Promise<unknown> {
	try {
		return await response.json();
	} catch {
		throw createAuthError(fallbackMessage, response.status);
	}
}

function isValidAuthSession(payload: unknown): payload is AuthSession {
	return (
		typeof payload === 'object' &&
		payload !== null &&
		'user' in payload &&
		typeof payload.user === 'object' &&
		payload.user !== null &&
		'access_token' in payload &&
		typeof payload.access_token === 'string' &&
		payload.access_token.trim().length > 0
	);
}

function isValidRefreshSession(payload: unknown): payload is RefreshSession {
	return (
		typeof payload === 'object' &&
		payload !== null &&
		'access_token' in payload &&
		typeof payload.access_token === 'string' &&
		payload.access_token.trim().length > 0
	);
}

function hasUsableUser(user: User | null | undefined): user is User {
	return typeof user === 'object' && user !== null;
}

function getSafeLoginRedirectUrl(loginRedirectUrl?: string | null): string {
	if (!loginRedirectUrl || loginRedirectUrl === '/') {
		return DEFAULT_LOGIN_REDIRECT_URL;
	}

	return loginRedirectUrl;
}

function getRequestUrl(resource: RequestInfo | URL): string {
	if (typeof resource === 'string') {
		return resource;
	}

	if (resource instanceof URL) {
		return resource.toString();
	}

	return resource.url;
}

function shouldBypassTokenRefresh(resource: RequestInfo | URL, config?: RequestInit): boolean {
	const requestHeaders = new Headers(resource instanceof Request ? resource.headers : undefined);
	const configHeaders = new Headers(config?.headers);
	const requestUrl = getRequestUrl(resource).toLowerCase();

	configHeaders.forEach((value, key) => {
		requestHeaders.set(key, value);
	});

	return (
		requestHeaders.get(AUTH_REFRESH_BYPASS_HEADER)?.toLowerCase() === 'true' ||
		requestUrl.includes('/api/auth/login') ||
		requestUrl.includes('/api/auth/refresh')
	);
}

function JwtAuthProvider(props: FuseAuthProviderComponentProps) {
	const { ref, children, onAuthStateChanged } = props;
	// Access the global QueryClient to clear cache on auth boundary changes
	const queryClient = useQueryClient();

	// Token + user live in either localStorage (Remember Me on) or sessionStorage
	// (Remember Me off). getAuthItem consults both. The setters preserve the existing
	// persistence class so token rotation never accidentally upgrades or downgrades the
	// user's choice.
	const tokenStorageValue = getAuthItem('accessToken');
	const rawUserStorageValue = getAuthItem('loggedUser');
	const userStorageValue: User | null = useMemo(() => {
		if (!rawUserStorageValue) return null;

		try {
			return JSON.parse(rawUserStorageValue) as User;
		} catch {
			return null;
		}
	}, [rawUserStorageValue]);

	const setTokenStorageValue = useCallback((value: string) => {
		setAuthItem('accessToken', value, isPersistentSession());
	}, []);
	const setUserStorageValue = useCallback((value: User) => {
		setAuthItem('loggedUser', JSON.stringify(value), isPersistentSession());
	}, []);

	// Track refresh timeout for proactive refresh
	const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const originalFetchRef = useRef<typeof window.fetch | null>(null);
	const isFetchInterceptedRef = useRef(false);

	/**
	 * Fuse Auth Provider State
	 */
	const [authState, setAuthState] = useState<FuseAuthProviderState<User>>({
		authStatus: 'configuring',
		isAuthenticated: false,
		user: userStorageValue || null // Initialize with stored user if available
	});

	/**
	 * Schedule proactive token refresh before expiration
	 */
	const clearScheduledRefresh = useCallback(() => {
		if (refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
			refreshTimeoutRef.current = null;
		}
	}, []);

	const restoreFetch = useCallback(() => {
		if (typeof window === 'undefined' || !isFetchInterceptedRef.current || !originalFetchRef.current) {
			return;
		}

		window.fetch = originalFetchRef.current;
		isFetchInterceptedRef.current = false;
	}, []);

	const resetAuthBoundary = useCallback(() => {
		clearScheduledRefresh();
		restoreFetch();
		clearAuthStorage();
		removeGlobalHeaders(['Authorization']);
		updateApiClientAuth(null);
		setAuthState({
			authStatus: 'unauthenticated',
			isAuthenticated: false,
			user: null
		});
	}, [clearScheduledRefresh, restoreFetch]);

	const scheduleProactiveRefresh = useCallback(
		(token: string) => {
			// Clear any existing timeout
			clearScheduledRefresh();

			try {
				const timeUntilExpiry = getTimeUntilExpiry(token);

				if (!timeUntilExpiry) {
					console.error('[Auth] Could not determine token expiry, skipping proactive refresh');
					return;
				}

				// Refresh 5 minutes before expiration (300000ms)
				const REFRESH_THRESHOLD = 5 * 60 * 1000;
				const timeUntilRefresh = Math.max(0, timeUntilExpiry - REFRESH_THRESHOLD);

				console.error(
					`[Auth] Scheduling proactive refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`
				);

				refreshTimeoutRef.current = setTimeout(async () => {
					console.error('[Auth] Proactive token refresh triggered');
					try {
						const response = await authRefreshToken();
						const sessionData = await parseAuthResponsePayload(response, 'Session refresh failed');

						if (!response.ok || !isValidRefreshSession(sessionData)) {
							throw createAuthError(
								extractAuthErrorMessage(sessionData, 'Session refresh failed'),
								response.status
							);
						}

						console.error('[Auth] Proactive refresh successful');
						setTokenStorageValue(sessionData.access_token);

						if (sessionData.user) {
							setUserStorageValue(sessionData.user);
							setAuthState((prev) => ({
								...prev,
								user: sessionData.user
							}));
						}

						setGlobalHeaders({
							Authorization: `Bearer ${sessionData.access_token}`
						});
						updateApiClientAuth(sessionData.access_token);

						// Schedule next proactive refresh
						scheduleProactiveRefresh(sessionData.access_token);
					} catch (error) {
						console.error('[Auth] Proactive refresh error:', error);
						resetAuthBoundary();
					}
				}, timeUntilRefresh);
			} catch (error) {
				console.error('[Auth] Failed to schedule proactive refresh:', error);
			}
		},
		[clearScheduledRefresh, resetAuthBoundary, setTokenStorageValue, setUserStorageValue]
	);

	/**
	 * Clean up refresh timeout on unmount
	 */
	useEffect(() => {
		return () => {
			clearScheduledRefresh();
			restoreFetch();
		};
	}, [clearScheduledRefresh, restoreFetch]);

	/**
	 * Watch for changes in the auth state
	 * and pass them to the FuseAuthProvider
	 */
	useEffect(() => {
		if (onAuthStateChanged) {
			onAuthStateChanged(authState);
		}
	}, [authState, onAuthStateChanged]);

	/**
	 * Attempt to auto login with the stored token
	 */
	useEffect(() => {
		const attemptAutoLogin = async () => {
			const accessToken = tokenStorageValue;
			const storedUser = userStorageValue;

			// If we have both token and user, and token is valid, authenticate immediately
			if (accessToken && storedUser && isTokenValid(accessToken)) {
				console.error('[Auth] Restoring session with valid token');

				// Fetch navigation to set correct loginRedirectUrl
				let loginRedirectUrl = getSafeLoginRedirectUrl(storedUser.loginRedirectUrl);
				try {
					setGlobalHeaders({ Authorization: `Bearer ${accessToken}` });
					updateApiClientAuth(accessToken);
					// Clear cached navigation so the correct user's nav is fetched
					navigationService.clearCache();
					const navigation = await navigationService.getNavigation();
					const firstUrl = FuseNavigationHelper.getLandingUrl(navigation, storedUser?.role);

					if (firstUrl) {
						loginRedirectUrl = firstUrl;
					}
				} catch (navError) {
					console.error('Could not fetch navigation for auto-login, using stored redirect:', navError);
				}

				const userWithRedirect = {
					...storedUser,
					loginRedirectUrl
				};

				setAuthState({
					authStatus: 'authenticated',
					isAuthenticated: true,
					user: userWithRedirect
				});
				setUserStorageValue(userWithRedirect);
				setGlobalHeaders({ Authorization: `Bearer ${accessToken}` });
				updateApiClientAuth(accessToken);

				// Schedule proactive refresh if needed
				scheduleProactiveRefresh(accessToken);

				return true;
			}

			// If we have a token but it's expired or no user data, try to refresh
			if (accessToken) {
				try {
					console.error('[Auth] Token expired or invalid, attempting refresh...');
					const response = await authRefreshToken();

					const sessionData = await parseAuthResponsePayload(response, 'Session refresh failed');

					if (response.ok && isValidRefreshSession(sessionData)) {
						const user = sessionData.user || storedUser;

						if (!hasUsableUser(user)) {
							console.error('[Auth] Token refresh returned no user context, clearing session');
							return false;
						}

						console.error('[Auth] Token refresh successful, session restored');

						// Set auth headers first so navigation fetch works
						setGlobalHeaders({
							Authorization: `Bearer ${sessionData.access_token}`
						});
						updateApiClientAuth(sessionData.access_token);

						// Fetch navigation for loginRedirectUrl
						let loginRedirectUrl = getSafeLoginRedirectUrl(user.loginRedirectUrl);
						try {
							// Clear cached navigation so the correct user's nav is fetched
							navigationService.clearCache();
							const navigation = await navigationService.getNavigation();
							const firstUrl = FuseNavigationHelper.getLandingUrl(navigation, user?.role);

							if (firstUrl) {
								loginRedirectUrl = firstUrl;
							}
						} catch (navError) {
							console.error('Could not fetch navigation for token refresh:', navError);
						}

						const userWithRedirect = {
							...user,
							loginRedirectUrl
						};

						setAuthState({
							authStatus: 'authenticated',
							isAuthenticated: true,
							user: userWithRedirect
						});
						setTokenStorageValue(sessionData.access_token);
						setUserStorageValue(userWithRedirect);

						// Schedule proactive refresh for new token
						scheduleProactiveRefresh(sessionData.access_token);

						return true;
					}

					console.error('[Auth] Token refresh failed, user must re-login');
				} catch (error) {
					if (error instanceof HTTPError) {
						console.error('[Auth] Token refresh failed with status:', error.response.status);
					} else {
						console.error('[Auth] Token refresh failed:', error);
					}
				}
			}

			return false;
		};

		if (authState.authStatus === 'configuring') {
			attemptAutoLogin()
				.then((success) => {
					if (!success) {
						// Clear invalid/expired data and set to unauthenticated
						console.error('[Auth] Auto-login failed, clearing stored data');
						resetAuthBoundary();
					}
				})
				.catch((error) => {
					console.error('[Auth] Auto-login failed:', error);
					// Ensure we set to unauthenticated if auto-login fails completely
					resetAuthBoundary();
				});
		}
	}, [
		tokenStorageValue,
		userStorageValue,
		authState.authStatus,
		resetAuthBoundary,
		scheduleProactiveRefresh,
		setUserStorageValue,
		setTokenStorageValue
	]);

	/**
	 * Sign in
	 */
	const signIn: JwtAuthContextType['signIn'] = useCallback(
		async (credentials) => {
			try {
				const response = await authSignIn(credentials);
				const sessionPayload = await parseAuthResponsePayload(response, 'Sign in failed');

				if (!response.ok) {
					clearAuthStorage();
					throw createAuthError(extractAuthErrorMessage(sessionPayload, 'Sign in failed'), response.status);
				}

				if (!isValidAuthSession(sessionPayload)) {
					clearAuthStorage();
					throw createAuthError('Login response was invalid. Please try again.', response.status);
				}

				const session = sessionPayload;

				// Set auth and token first
				setTokenStorageValue(session.access_token);
				setGlobalHeaders({ Authorization: `Bearer ${session.access_token}` });
				updateApiClientAuth(session.access_token);

				// Fetch navigation to determine first available URL
				let loginRedirectUrl = DEFAULT_LOGIN_REDIRECT_URL;
				try {
					// Clear any previously cached navigation so new user's roles take effect
					navigationService.clearCache();
					const navigation = await navigationService.getNavigation();
					const firstUrl = FuseNavigationHelper.getLandingUrl(navigation, session.user?.role);

					if (firstUrl) {
						loginRedirectUrl = firstUrl;
					}
				} catch (navError) {
					console.error('Could not fetch navigation for redirect, using default:', navError);
				}

				// Update user with dynamic redirect URL
				const userWithRedirect = {
					...session.user,
					loginRedirectUrl
				};

				setAuthState({
					authStatus: 'authenticated',
					isAuthenticated: true,
					user: userWithRedirect
				});
				setUserStorageValue(userWithRedirect);
				scheduleProactiveRefresh(session.access_token);

				return { ...session, user: userWithRedirect };
			} catch (error) {
				if (error instanceof HTTPError) {
					console.error('Sign in failed:', error.response.status);
				}

				throw error;
			}
		},
		[scheduleProactiveRefresh, setTokenStorageValue, setUserStorageValue]
	);

	/**
	 * Sign up - Not implemented for Dorat
	 */
	const signUp: JwtAuthContextType['signUp'] = useCallback(async (_data) => {
		throw new Error('Sign up is not available in Dorat');
	}, []);

	/**
	 * Sign out
	 */
	const signOut: JwtAuthContextType['signOut'] = useCallback(() => {
		// Clear all cached queries and mutations to avoid leaking previous user data
		try {
			queryClient.clear();
		} catch (_error) {
			// noop – clearing cache is best-effort
		}
		// Also clear navigation service cache (separate from TanStack Query)
		try {
			navigationService.clearCache();
		} catch (_error) {
			// noop
		}
		resetAuthBoundary();
	}, [queryClient, resetAuthBoundary]);

	/**
	 * Update user
	 */
	const updateUser: JwtAuthContextType['updateUser'] = useCallback(async (_user) => {
		try {
			const response = await authUpdateDbUser(_user);
			return response;
		} catch (error) {
			if (error instanceof HTTPError) {
				console.error('Update user failed:', error.response.status);
			}

			throw error;
		}
	}, []);

	/**
	 * Refresh access token
	 */
	const refreshToken: JwtAuthContextType['refreshToken'] = useCallback(async () => {
		try {
			const response = await authRefreshToken();
			return response;
		} catch (error) {
			if (error instanceof HTTPError) {
				console.error('Token refresh failed:', error.response.status);
			}

			throw error;
		}
	}, []);

	/**
	 * Auth Context Value
	 */
	const authContextValue = useMemo(
		() =>
			({
				...authState,
				signIn,
				signUp,
				signOut,
				updateUser,
				refreshToken
			}) as JwtAuthContextType,
		[authState, signIn, signUp, signOut, updateUser, refreshToken]
	);

	/**
	 * Expose methods to the FuseAuthProvider
	 */
	useImperativeHandle(ref, () => ({
		signOut,
		updateUser
	}));

	/**
	 * Intercept fetch requests to refresh the access token on 401
	 */
	const interceptFetch = useCallback(() => {
		if (typeof window === 'undefined' || isFetchInterceptedRef.current) {
			return;
		}

		originalFetchRef.current = window.fetch;
		const originalFetch = originalFetchRef.current;
		isFetchInterceptedRef.current = true;

		window.fetch = async (...args) => {
			const [resource, config] = args;
			const bypassTokenRefresh = shouldBypassTokenRefresh(resource, config);

			let response: Response;
			let didRefresh = false;

			try {
				response = await originalFetch(resource, config);

				// Check for new token in response headers
				const newAccessToken = response.headers.get('New-Access-Token');

				if (newAccessToken) {
					console.error('[Auth] New access token received from response headers');
					setGlobalHeaders({ Authorization: `Bearer ${newAccessToken}` });
					setTokenStorageValue(newAccessToken);
					updateApiClientAuth(newAccessToken);

					// Schedule proactive refresh for new token
					scheduleProactiveRefresh(newAccessToken);
				}

				// Handle 401 - attempt token refresh and retry
				if (response.status === 401 && !didRefresh && !bypassTokenRefresh) {
					console.error('[Auth] 401 detected, attempting token refresh and retry...');

					try {
						const refreshResponse = await authRefreshToken();

						const sessionData = await parseAuthResponsePayload(refreshResponse, 'Session refresh failed');

						if (refreshResponse.ok && isValidRefreshSession(sessionData)) {
							console.error('[Auth] Token refreshed successfully, retrying original request');
							didRefresh = true;

							// Update tokens and auth state
							setTokenStorageValue(sessionData.access_token);

							if (sessionData.user) {
								setUserStorageValue(sessionData.user);
								setAuthState((prev) => ({
									...prev,
									user: sessionData.user
								}));
							}

							setGlobalHeaders({
								Authorization: `Bearer ${sessionData.access_token}`
							});
							updateApiClientAuth(sessionData.access_token);

							// Schedule proactive refresh for new token
							scheduleProactiveRefresh(sessionData.access_token);

							// Retry the original request with new token
							const newConfig = {
								...config,
								headers: {
									...(config?.headers || {}),
									Authorization: `Bearer ${sessionData.access_token}`
								}
							};

							response = await originalFetch(resource, newConfig);

							// If retry still returns 401, sign out
							if (response.status === 401) {
								console.error('[Auth] Retry failed with 401, signing out');
								signOut();
							}

							return response;
						}

						// Refresh failed, sign out
						console.error('[Auth] Token refresh failed on 401, signing out');
						signOut();
					} catch (refreshError) {
						console.error('[Auth] Error during token refresh on 401:', refreshError);
						signOut();
					}
				}

				return response;
			} catch (error) {
				if (error instanceof HTTPError && error.response.status === 401) {
					console.error('[Auth] HTTPError 401, signing out');
					signOut();
				}

				throw error;
			}
		};
	}, [setTokenStorageValue, setUserStorageValue, signOut, scheduleProactiveRefresh]);

	useEffect(() => {
		if (authState.isAuthenticated) {
			interceptFetch();
			return () => {
				restoreFetch();
			};
		}

		restoreFetch();
	}, [authState.isAuthenticated, interceptFetch, restoreFetch]);

	return <JwtAuthContext value={authContextValue}>{children}</JwtAuthContext>;
}

export default JwtAuthProvider;
